import {Inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {map, Observable} from "rxjs";
import {FormDefinition, FormStatus} from "../shared/models/form-schema.model";
import {ServerEndpoints} from "../../../server.endpoints";

/**
 * Transfer object exchanged with the Forms microservice. Mirrors the backend
 * `FormDocumentResource`: a queryable envelope wrapping the canonical
 * {@link FormDefinition} document.
 */
export interface FormDocumentResource {
  key: string;
  version: number;
  title: string;
  status: string;
  definition: FormDefinition;
  companyId?: string | null;
  shardId?: string | null;
}

/**
 * Thin HTTP client for the Forms microservice's canonical definition API
 * (`/api/forms/v2025.1/definition`). Requests go through the gateway under the SPA's single `BASE_URL`
 * origin, so the auth interceptor attaches the context token automatically.
 */
@Injectable({
  providedIn: 'root'
})
export class FormHttpService {
  constructor(private httpClient: HttpClient, @Inject("BASE_URL") private baseUrl: string) {
  }

  private get base(): string {
    return this.baseUrl + ServerEndpoints.Forms_Definition__v2025_1;
  }

  listDefinitions(): Observable<FormDefinition[]> {
    return this.httpClient.get<FormDocumentResource[]>(this.base)
      .pipe(map(list => list.map(FormHttpService.fromResource)));
  }

  getDefinition(id: string): Observable<FormDefinition> {
    return this.httpClient.get<FormDocumentResource>(`${this.base}/${id}`)
      .pipe(map(FormHttpService.fromResource));
  }

  upsertDefinition(def: FormDefinition): Observable<FormDefinition> {
    return this.httpClient.post<FormDocumentResource>(this.base, FormHttpService.toResource(def))
      .pipe(map(FormHttpService.fromResource));
  }

  deleteDefinition(id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.base}/${id}`);
  }

  // --- mapping ---------------------------------------------------------------
  private static toResource(def: FormDefinition): FormDocumentResource {
    return {
      key: def.id,
      version: def.version,
      title: def.title,
      status: def.status,
      definition: def
    };
  }

  /** Reconcile the stored document with the server-authoritative envelope (version/status). */
  private static fromResource(resource: FormDocumentResource): FormDefinition {
    return {
      ...resource.definition,
      id: resource.key,
      version: resource.version,
      status: resource.status as FormStatus
    };
  }
}
